import React, { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  FileSearch,
  FileText,
  Folder,
  Info,
  Layers,
  Monitor,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import TablePdf from "./components/TablePdf";
import TenantSelection from "../Components/TenantSelection";
import DeviceSelection from "./components/DeviceSelection";
import UserSelection from "./components/UserSelection";
import ResourceTypeSelection from "./components/ResourceTypeSelection";
import SelectOS from "../Components/SelectOS";
import ProfileSelection from "./components/ProfileSelection";
import { myLocalStorage } from "../../../components/StorageHelper";
import "./OverProvisioning.css";

const steps = [
  {
    label: "Tenant",
    title: "Select tenant",
    icon: Building2,
    description: "Choose the tenant whose endpoint access you want to review.",
    guidance:
      "The selected tenant determines which devices and comparison profiles are available.",
  },
  {
    label: "Operating system",
    title: "Select operating system",
    icon: Monitor,
    description: "Choose the operating system for this report.",
    guidance:
      "Devices and profiles are filtered by the operating system you select.",
  },
  {
    label: "Devices",
    title: "Select devices",
    icon: Monitor,
    description: "Select one or more devices to include in the comparison.",
    guidance:
      "The report reviews the selected users against each device in your scope.",
  },
  {
    label: "Users",
    title: "Select users",
    icon: Users,
    description: "Choose the device users whose access you want to compare.",
    guidance:
      "Users are loaded from the devices selected in the previous step.",
  },
  {
    label: "Resource types",
    title: "Select resource types",
    icon: Layers,
    description: "Choose the access categories to include in your report.",
    guidance:
      "Applications, folders and privileges are compared separately. Each category can be exported as a PDF.",
  },
  {
    label: "Comparison profile",
    title: "Select comparison profile",
    icon: ShieldCheck,
    description: "Choose the profile to use as your access baseline.",
    guidance:
      "Results identify enabled access that matches resources blocked by this profile. Generating a report does not change permissions.",
  },
];

const resourceGuides = [
  {
    title: "Applications",
    icon: FileText,
    description:
      "Enabled application access that matches applications blocked by the comparison profile.",
  },
  {
    title: "Folders",
    icon: Folder,
    description:
      "Enabled folder access that matches folders blocked by the comparison profile.",
  },
  {
    title: "Privileges",
    icon: ShieldCheck,
    description:
      "Enabled group membership that matches privileges blocked by the comparison profile.",
  },
];

const OverProvisioning = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTenant, setSelectedTenant] = useState(
    myLocalStorage.getItem("latestTenant"),
  );
  const [activeDevicesList, setActiveDevicesList] = useState([]);
  const [activeUsersList, setActiveUsersList] = useState([]);

  const [activeResourceType, setActiveResourceType] = useState([]);
  const [selectedOS, setSelectedOS] = useState("");

  const [selectedProfile, setSelectedProfile] = useState(null);

  const [stepsCompleted, setStepsCompleted] = useState(false);

  const handleNext = () => {
    if (activeStep === 0 && selectedTenant) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else if (activeStep === 1 && selectedOS) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else if (activeStep === 2 && activeDevicesList.length) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else if (activeStep === 3 && activeUsersList.length) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else if (activeStep === 4 && activeResourceType.length) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleFinish = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setStepsCompleted(true);
  };

  const handleBack = () => {
    if (stepsCompleted) setStepsCompleted(false);

    setActiveStep((prevActiveStep) => prevActiveStep - 1);

    if (activeStep === 2) setActiveDevicesList([]);
    if (activeStep === 3) {
      setActiveDevicesList([]);
      setActiveUsersList([]);
      setActiveResourceType([]);
    } else if (activeStep === 4) {
      setActiveUsersList([]);
      setActiveResourceType([]);
    } else if (activeStep === 5) setActiveResourceType([]);
  };

  const handleReset = () => {
    if (stepsCompleted) setStepsCompleted(false);
    setActiveDevicesList([]);
    setActiveUsersList([]);
    setActiveResourceType([]);
    setSelectedProfile(null);

    setActiveStep(0);
  };

  const currentStep = steps[Math.min(activeStep, steps.length - 1)];
  const StepIcon = currentStep.icon;
  const continueDisabled =
    (activeStep === 0 && !selectedTenant) ||
    (activeStep === 1 && !selectedOS) ||
    (activeStep === 2 && !activeDevicesList.length) ||
    (activeStep === 3 && !activeUsersList.length) ||
    (activeStep === 4 && !activeResourceType.length) ||
    (activeStep === 5 && !selectedProfile);

  const scope = [
    {
      label: "Tenant",
      value: selectedTenant?.tenantName || "Not selected",
      icon: Building2,
    },
    {
      label: "Operating system",
      value: selectedOS || "Not selected",
      icon: Monitor,
    },
    {
      label: "Devices",
      value: activeDevicesList.length
        ? activeDevicesList.map((device) => device.dNSHostName).join(", ")
        : "Not selected",
      icon: Monitor,
    },
    {
      label: "Users",
      value: activeUsersList.length
        ? activeUsersList.map((user) => user.cn).join(", ")
        : "Not selected",
      icon: Users,
    },
    {
      label: "Resource types",
      value: activeResourceType.join(", ") || "Not selected",
      icon: Layers,
    },
    {
      label: "Comparison profile",
      value: selectedProfile?.name || "Not selected",
      icon: ShieldCheck,
    },
  ];

  return (
    <div className="overprovisioning-page">
      <section className="op-card op-intro">
        <div className="op-heading">
          <span className="op-icon">
            <FileSearch aria-hidden="true" />
          </span>
          <div>
            <h1>Over-provisioning report</h1>
            <p>
              Compare endpoint access with a policy profile to identify access
              that needs review.
            </p>
          </div>
        </div>
        <span className="op-badge">
          <ShieldCheck aria-hidden="true" /> Read-only comparison
        </span>
      </section>

      <section
        className="op-card op-progress"
        aria-label="Report setup progress"
      >
        <ol>
          {steps.map((step, index) => (
            <li
              key={step.label}
              className={
                index < activeStep
                  ? "is-complete"
                  : index === activeStep
                    ? "is-active"
                    : ""
              }
              aria-current={index === activeStep ? "step" : undefined}
            >
              <span className="op-step-number">
                {index < activeStep ? <Check aria-hidden="true" /> : index + 1}
              </span>
              <span>{step.label}</span>
            </li>
          ))}
        </ol>
      </section>

      <div className="op-workspace">
        <section className="op-card op-setup">
          <div className="op-heading">
            <span className="op-icon">
              {stepsCompleted ? (
                <Check aria-hidden="true" />
              ) : (
                <StepIcon aria-hidden="true" />
              )}
            </span>
            <div>
              <span className="op-eyebrow">
                {stepsCompleted
                  ? "Setup complete"
                  : `Step ${activeStep + 1} of ${steps.length}`}
              </span>
              <h2>
                {stepsCompleted
                  ? "Your report is ready to review"
                  : currentStep.title}
              </h2>
              <p>
                {stepsCompleted
                  ? "Review the comparison results below or start a new report."
                  : currentStep.description}
              </p>
            </div>
          </div>

          <div className="op-selection-area">
            {stepsCompleted ? (
              <div className="op-complete-state">
                <span className="op-complete-icon">
                  <FileSearch aria-hidden="true" />
                </span>
                <h3>Comparison scope confirmed</h3>
                <p>
                  {activeDevicesList.length}{" "}
                  {activeDevicesList.length === 1 ? "device" : "devices"} ·{" "}
                  {activeUsersList.length}{" "}
                  {activeUsersList.length === 1 ? "user" : "users"} ·{" "}
                  {activeResourceType.length}{" "}
                  {activeResourceType.length === 1
                    ? "resource type"
                    : "resource types"}
                </p>
                <span className="op-badge">{selectedProfile?.name}</span>
              </div>
            ) : activeStep === 0 ? (
              <TenantSelection
                selectedTenant={selectedTenant}
                setSelectedTenant={setSelectedTenant}
              />
            ) : activeStep === 1 ? (
              <SelectOS selectedOS={selectedOS} setSelectedOS={setSelectedOS} />
            ) : activeStep === 2 ? (
              <DeviceSelection
                os={selectedOS}
                selectedTenant={selectedTenant}
                activeDevicesList={activeDevicesList}
                setActiveDevicesList={setActiveDevicesList}
              />
            ) : activeStep === 3 ? (
              <UserSelection
                activeDevicesList={activeDevicesList}
                setActiveUsersList={setActiveUsersList}
                activeUsersList={activeUsersList}
                selectedTenantName={selectedTenant?.tenantName}
              />
            ) : activeStep === 4 ? (
              <ResourceTypeSelection
                activeResourceType={activeResourceType}
                setActiveResourceType={setActiveResourceType}
              />
            ) : (
              <ProfileSelection
                os={selectedOS}
                selectedTenant={selectedTenant}
                setSelectedProfile={setSelectedProfile}
                selectedProfile={selectedProfile}
              />
            )}
          </div>

          <div className="op-guidance">
            <Info aria-hidden="true" />
            <p>
              {stepsCompleted
                ? "Results show matches within your selected scope. Review each finding before deciding whether access should change."
                : currentStep.guidance}
            </p>
          </div>
          <div className="op-setup-actions">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              <ArrowLeft aria-hidden="true" /> Back
            </Button>
            {stepsCompleted ? (
              <Button onClick={handleReset}>Start new report</Button>
            ) : (
              <Button
                disabled={continueDisabled}
                onClick={
                  activeStep === steps.length - 1 ? handleFinish : handleNext
                }
              >
                {activeStep === steps.length - 1
                  ? "Generate report"
                  : "Continue"}
                <ArrowRight aria-hidden="true" />
              </Button>
            )}
          </div>
        </section>

        <aside className="op-card op-scope">
          <div className="op-heading">
            <span className="op-icon">
              <Layers aria-hidden="true" />
            </span>
            <div>
              <h2>Report scope</h2>
              <p>Your selections, as you build the report.</p>
            </div>
          </div>
          <dl>
            {scope.map((item) => (
              <div key={item.label} className="op-scope-item">
                <item.icon aria-hidden="true" />
                <div>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              </div>
            ))}
          </dl>
        </aside>
      </div>

      {stepsCompleted && (
        <TablePdf
          selectedProfile={selectedProfile}
          selectedTenant={selectedTenant}
          activeUsersList={activeUsersList}
          activeDevicesList={activeDevicesList}
          activeResourceType={activeResourceType}
        />
      )}

      <section className="op-card op-explainer">
        <div className="op-heading">
          <span className="op-icon">
            <ShieldCheck aria-hidden="true" />
          </span>
          <div>
            <h2>What this report compares</h2>
            <p>
              Enabled access, including inherited access, is matched against
              resources blocked by your chosen profile.
            </p>
          </div>
        </div>
        <div className="op-resource-guides">
          {resourceGuides.map((guide) => (
            <div key={guide.title}>
              <span className="op-icon">
                <guide.icon aria-hidden="true" />
              </span>
              <div>
                <h3>{guide.title}</h3>
                <p>{guide.description}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="op-report-note">
          <FileText aria-hidden="true" />
          <p>
            Results include the resource, device and user. Export a PDF for each
            selected resource type to support your access review.
          </p>
        </div>
      </section>
    </div>
  );
};

export default OverProvisioning;
